import React from 'react';
import { useVideoCall } from '../hooks/useVideoCall';
import DeviceMenu from './VideoCall/DeviceMenu';
import CallBtn from './VideoCall/CallBtn';
import ConfirmEndCallModal from './VideoCall/ConfirmEndCallModal';

export default function VideoCallModal({
  isOpen, onClose, lessonId,
  isStudent = false, autoJoin = false, autoStart = false,
  initialVideoDeviceId = null, initialAudioDeviceId = null, initialAudioOutputDeviceId = null,
}) {
  const {
    error, status, channelReady,
    micOn, camOn,
    currentVideoDeviceId, currentAudioDeviceId, currentAudioOutputDeviceId,
    sizeIdx, isFocusMode, circleSize,
    iceState, showRelayBtn, speakerSupported,
    cameras, microphones, speakers,
    localVideoRef, remoteVideoRef,
    toggleMic, toggleCam,
    switchCamera, switchMic, switchSpeaker,
    cycleSize, retryRelay, joinCall,
    confirmEndOpen, requestHangUp, cancelHangUp, hangUp,
  } = useVideoCall({
    isOpen, onClose, lessonId,
    isStudent, autoJoin, autoStart,
    initialVideoDeviceId, initialAudioDeviceId, initialAudioOutputDeviceId,
  });

  if (!isOpen) return null;

  // ── Focus mode (mode 2): fullscreen blur overlay, two large circles ──────
  if (isFocusMode) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 2147483647,
        backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 40,
      }}>
        {(status !== 'in_call' || (iceState !== 'connected' && iceState !== 'completed')) && (
          <div style={{ position: 'absolute', top: 20, fontSize: 12, color: 'rgba(255,255,255,0.6)', userSelect: 'none' }}>
            {status === 'starting' ? 'Starting…' : status === 'joining' ? 'Joining…' : status === 'in_call' ? 'Connecting…' : isStudent ? 'Waiting…' : 'Ready'}
          </div>
        )}
        {error && (
          <div style={{ position: 'absolute', top: 44, fontSize: 12, color: '#f87171', textAlign: 'center', padding: '0 16px' }}>{error}</div>
        )}
        {showRelayBtn && (
          <button onClick={retryRelay} style={{
            position: 'absolute', top: 66,
            padding: '6px 16px', fontSize: 12, fontWeight: 600,
            background: '#f59e0b', color: '#fff', border: 'none',
            borderRadius: 999, cursor: 'pointer',
          }}>
            Force relay (VPN fix)
          </button>
        )}

        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 40 }}>
          {/* Remote circle */}
          <div style={{ position: 'relative' }}>
            <video
              ref={remoteVideoRef}
              autoPlay playsInline
              style={{
                width: 550, height: 550, borderRadius: '50%',
                objectFit: 'cover', background: '#1a1a1a',
                boxShadow: '0 12px 48px rgba(0,0,0,0.6)',
                display: 'block',
              }}
            />
            <div style={{
              position: 'absolute', bottom: 6, right: 6,
              width: 14, height: 14, borderRadius: '50%',
              background: (iceState === 'connected' || iceState === 'completed') ? '#22c55e' : iceState === 'checking' ? '#f59e0b' : '#6b7280',
              border: '3px solid rgba(255,255,255,0.9)',
            }} />
          </div>

          {/* Local circle */}
          <div style={{ position: 'relative' }}>
            <video
              ref={localVideoRef}
              autoPlay muted playsInline
              style={{
                width: 550, height: 550, borderRadius: '50%',
                objectFit: 'cover', background: '#111',
                boxShadow: '0 12px 48px rgba(0,0,0,0.6)',
                display: 'block',
                opacity: camOn ? 1 : 0.35,
                transform: 'scaleX(-1)',
              }}
            />
          </div>
        </div>

        {/* Controls — pinned to bottom */}
        <div style={{ position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)' }}>
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center',
            background: 'rgba(20,20,20,0.8)',
            backdropFilter: 'blur(12px)',
            borderRadius: 999, padding: '8px 14px',
          }}>
            <CallBtn active={micOn} onClick={toggleMic} title={micOn ? 'Mute' : 'Unmute'} large>
              {micOn
                ? <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M5 3a3 3 0 0 1 6 0v5a3 3 0 0 1-6 0z"/><path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5"/></svg>
                : <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M13 8c0 .564-.094 1.107-.266 1.613l-.814-.814A4 4 0 0 0 12 8V7a.5.5 0 0 1 1 0zm-5 4c.818 0 1.578-.245 2.212-.667l.718.719a5 5 0 0 1-2.43.923V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 1 0v1a4 4 0 0 0 4 4m3-9v4.879l-1-1V3a2 2 0 0 0-3.997-.118l-.845-.845A3.001 3.001 0 0 1 11 3"/><path d="m9.486 10.607-.748-.748A2 2 0 0 1 6 8v-.878l-1-1V8a3 3 0 0 0 4.486 2.607m-7.84-9.253 12 12 .708-.708-12-12z"/></svg>
              }
            </CallBtn>
            <DeviceMenu
              large
              sections={[
                { label: 'Microphone', devices: microphones, currentDeviceId: currentAudioDeviceId, onSelect: switchMic },
                ...(speakerSupported ? [{ label: 'Speaker', devices: speakers, currentDeviceId: currentAudioOutputDeviceId, onSelect: switchSpeaker }] : []),
              ]}
            />

            <CallBtn active={camOn} onClick={toggleCam} title={camOn ? 'Hide camera' : 'Show camera'} large>
              {camOn
                ? <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M0 5a2 2 0 0 1 2-2h7.5a2 2 0 0 1 1.983 1.738l3.11-1.382A1 1 0 0 1 16 4.269v7.462a1 1 0 0 1-1.406.913l-3.111-1.382A2 2 0 0 1 9.5 13H2a2 2 0 0 1-2-2z"/></svg>
                : <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M10.961 12.365a2 2 0 0 0 .522-1.103l3.11 1.382A1 1 0 0 0 16 11.731V4.269a1 1 0 0 0-1.406-.913l-3.111 1.382A2 2 0 0 0 9.5 3H4.272l.714 1H9.5a1 1 0 0 1 1 1v6a1 1 0 0 1-.144.518zM1.428 4.18A1 1 0 0 0 1 5v6a1 1 0 0 0 1 1h5.014l.714 1H2a2 2 0 0 1-2-2V5c0-.675.334-1.272.847-1.634zM15 11.73l-3.5-1.555v-4.35L15 4.269zm-4.407 3.56-10-14 .814-.58 10 14z"/></svg>
              }
            </CallBtn>
            <DeviceMenu
              large
              sections={[{ label: 'Camera', devices: cameras, currentDeviceId: currentVideoDeviceId, onSelect: switchCamera }]}
            />

            {/* Exit focus mode */}
            <CallBtn onClick={cycleSize} active large title="Exit focus mode">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M.172 15.828a.5.5 0 0 0 .707 0l4.096-4.096V14.5a.5.5 0 1 0 1 0v-3.975a.5.5 0 0 0-.5-.5H1.5a.5.5 0 0 0 0 1h2.768L.172 15.121a.5.5 0 0 0 0 .707M15.828.172a.5.5 0 0 0-.707 0l-4.096 4.096V1.5a.5.5 0 1 0-1 0v3.975a.5.5 0 0 0 .5.5H14.5a.5.5 0 0 0 0-1h-2.768L15.828.879a.5.5 0 0 0 0-.707"/></svg>
            </CallBtn>

            <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.15)' }} />
            <CallBtn onClick={requestHangUp} danger large title="End call">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.68.68 0 0 0 .178.643l2.457 2.457a.68.68 0 0 0 .644.178l2.189-.547a1.75 1.75 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.6 18.6 0 0 1-7.01-4.42 18.6 18.6 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877z"/></svg>
            </CallBtn>
          </div>
        </div>

        <ConfirmEndCallModal isOpen={confirmEndOpen} onCancel={cancelHangUp} onConfirm={hangUp} />

        {isStudent && status === 'idle' && channelReady && (
          <button onClick={joinCall} style={{
            position: 'absolute', bottom: 100,
            padding: '8px 20px', fontSize: 13, fontWeight: 600,
            background: '#3b82f6', color: '#fff', border: 'none',
            borderRadius: 999, cursor: 'pointer',
          }}>
            Join Call
          </button>
        )}
      </div>
    );
  }

  // ── Normal modes (0 = small, 1 = medium): top-right floating panel ───────
  return (
    <div style={{
      position: 'fixed', right: 16, top: 64, zIndex: 2147483647,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    }}>
      {(status !== 'in_call' || (iceState !== 'connected' && iceState !== 'completed')) && (
        <div style={{ fontSize: 10, color: '#888', userSelect: 'none', textAlign: 'center' }}>
          {status === 'starting' ? 'Starting…' : status === 'joining' ? 'Joining…' : status === 'in_call' ? 'Connecting…' : isStudent ? 'Waiting…' : 'Ready'}
        </div>
      )}
      {error && <div style={{ fontSize: 11, color: '#f87171', maxWidth: 96, textAlign: 'center' }}>{error}</div>}
      {showRelayBtn && (
        <button onClick={retryRelay} style={{
          padding: '4px 10px', fontSize: 10, fontWeight: 600,
          background: '#f59e0b', color: '#fff', border: 'none',
          borderRadius: 999, cursor: 'pointer',
        }}>
          Force relay (VPN fix)
        </button>
      )}

      {/* Remote circle */}
      <div style={{ position: 'relative' }}>
        <video
          ref={remoteVideoRef}
          autoPlay playsInline
          style={{
            width: circleSize, height: circleSize, borderRadius: '50%',
            objectFit: 'cover', background: '#1a1a1a',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)', display: 'block',
          }}
        />
        <div style={{
          position: 'absolute', bottom: 2, right: 2,
          width: 10, height: 10, borderRadius: '50%',
          background: status === 'in_call' ? '#22c55e' : '#6b7280',
          border: '2px solid #fff',
        }} />
      </div>

      {/* Local circle */}
      <div style={{ position: 'relative' }}>
        <video
          ref={localVideoRef}
          autoPlay muted playsInline
          style={{
            width: circleSize, height: circleSize, borderRadius: '50%',
            objectFit: 'cover', background: '#111',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)', display: 'block',
            opacity: camOn ? 1 : 0.35,
            transform: 'scaleX(-1)',
          }}
        />
      </div>

      {/* Controls */}
      <div style={{
        display: 'flex', gap: 4, alignItems: 'center',
        background: 'rgba(20,20,20,0.75)',
        backdropFilter: 'blur(10px)',
        borderRadius: 999, padding: '4px 6px',
      }}>
        <CallBtn active={micOn} onClick={toggleMic} title={micOn ? 'Mute' : 'Unmute'}>
          {micOn
            ? <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path d="M5 3a3 3 0 0 1 6 0v5a3 3 0 0 1-6 0z"/><path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5"/></svg>
            : <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path d="M13 8c0 .564-.094 1.107-.266 1.613l-.814-.814A4 4 0 0 0 12 8V7a.5.5 0 0 1 1 0zm-5 4c.818 0 1.578-.245 2.212-.667l.718.719a5 5 0 0 1-2.43.923V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 1 0v1a4 4 0 0 0 4 4m3-9v4.879l-1-1V3a2 2 0 0 0-3.997-.118l-.845-.845A3.001 3.001 0 0 1 11 3"/><path d="m9.486 10.607-.748-.748A2 2 0 0 1 6 8v-.878l-1-1V8a3 3 0 0 0 4.486 2.607m-7.84-9.253 12 12 .708-.708-12-12z"/></svg>
          }
        </CallBtn>
        <DeviceMenu
          sections={[
            { label: 'Microphone', devices: microphones, currentDeviceId: currentAudioDeviceId, onSelect: switchMic },
            ...(speakerSupported ? [{ label: 'Speaker', devices: speakers, currentDeviceId: currentAudioOutputDeviceId, onSelect: switchSpeaker }] : []),
          ]}
        />

        <CallBtn active={camOn} onClick={toggleCam} title={camOn ? 'Hide camera' : 'Show camera'}>
          {camOn
            ? <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M0 5a2 2 0 0 1 2-2h7.5a2 2 0 0 1 1.983 1.738l3.11-1.382A1 1 0 0 1 16 4.269v7.462a1 1 0 0 1-1.406.913l-3.111-1.382A2 2 0 0 1 9.5 13H2a2 2 0 0 1-2-2z"/></svg>
            : <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M10.961 12.365a2 2 0 0 0 .522-1.103l3.11 1.382A1 1 0 0 0 16 11.731V4.269a1 1 0 0 0-1.406-.913l-3.111 1.382A2 2 0 0 0 9.5 3H4.272l.714 1H9.5a1 1 0 0 1 1 1v6a1 1 0 0 1-.144.518zM1.428 4.18A1 1 0 0 0 1 5v6a1 1 0 0 0 1 1h5.014l.714 1H2a2 2 0 0 1-2-2V5c0-.675.334-1.272.847-1.634zM15 11.73l-3.5-1.555v-4.35L15 4.269zm-4.407 3.56-10-14 .814-.58 10 14z"/></svg>
          }
        </CallBtn>
        <DeviceMenu
          sections={[{ label: 'Camera', devices: cameras, currentDeviceId: currentVideoDeviceId, onSelect: switchCamera }]}
        />

        {/* Cycle size: small → medium → focus → small */}
        <CallBtn onClick={cycleSize} active title={sizeIdx === 0 ? 'Enlarge' : sizeIdx === 1 ? 'Focus mode' : 'Shrink'}>
          {sizeIdx === 1
            ? /* fullscreen / enter-focus icon */
              <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path d="M1.5 1h5a.5.5 0 0 0 0-1h-5A1.5 1.5 0 0 0 0 1.5v5a.5.5 0 0 0 1 0V1.5L5.354 4.854a.5.5 0 1 0 .707-.707L2.707 1H7zm5 14H1.5a.5.5 0 0 1-.5-.5v-5a.5.5 0 0 1 1 0V13.5l3.354-3.354a.5.5 0 1 1 .707.707L2.707 14H7a.5.5 0 0 1 0 1zM15.5 1H11a.5.5 0 0 1 0-1h5a.5.5 0 0 1 .5.5v5a.5.5 0 0 1-1 0V1.5L12.146 4.854a.5.5 0 1 1-.707-.707L14.793 1H11zm0 14h-4.5a.5.5 0 0 1 0-1h3.793l-3.354-3.354a.5.5 0 1 1 .707-.707L15 13.293V9.5a.5.5 0 0 1 1 0v5a.5.5 0 0 1-.5.5z"/></svg>
            : /* expand icon */
              <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707m4.344-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707"/></svg>
          }
        </CallBtn>

        <div style={{ width: 1, height: 12, background: 'rgba(255,255,255,0.15)' }} />
        <CallBtn onClick={requestHangUp} danger title="End call">
          <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.68.68 0 0 0 .178.643l2.457 2.457a.68.68 0 0 0 .644.178l2.189-.547a1.75 1.75 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.6 18.6 0 0 1-7.01-4.42 18.6 18.6 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877z"/></svg>
        </CallBtn>
      </div>

      <ConfirmEndCallModal isOpen={confirmEndOpen} onCancel={cancelHangUp} onConfirm={hangUp} />

      {isStudent && status === 'idle' && channelReady && (
        <button onClick={joinCall} style={{
          padding: '5px 12px', fontSize: 11, fontWeight: 600,
          background: '#3b82f6', color: '#fff', border: 'none',
          borderRadius: 999, cursor: 'pointer',
        }}>
          Join Call
        </button>
      )}
    </div>
  );
}
